import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Save, Plus, Trash2, Star, Download, Package, CheckCircle2 } from 'lucide-react';
import { downloadRigZip } from '@/lib/telemetryZip';
import LockedField from '@/components/admin/LockedField';

const THEMES = [
  { value: 'none', label: 'Standard' },
  { value: 'porsche', label: 'Porsche' },
  { value: 'amg', label: 'AMG' },
];

const SIM_OPTIONS = [
  { value: 'auto', label: 'Auto-detect (any sim)' },
  { value: 'acc', label: 'Assetto Corsa Competizione' },
  { value: 'assetto', label: 'Assetto Corsa' },
];

const LS_SIM = 'mr_telemetry_sim';

export default function RigManagerPanel({ token, onTokenChange }) {
  const [rigs, setRigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [zipping, setZipping] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [adding, setAdding] = useState(false);
  const [sim, setSim] = useState('auto');

  useEffect(() => {
    // Any value left over from before sims could be pinned (iRacing was the old
    // default) falls back to auto-detect, so existing launchers keep working.
    const stored = localStorage.getItem(LS_SIM);
    setSim(SIM_OPTIONS.some((o) => o.value === stored) ? stored : 'auto');
  }, []);

  const saveSim = (v) => { setSim(v); localStorage.setItem(LS_SIM, v); };

  const load = () => {
    setLoading(true);
    base44.entities.RigLiveFeed.list('-sort_order', 50)
      .then((d) => setRigs((d || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const updateField = (id, field, value) => {
    setRigs((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const save = async (rig) => {
    setSaving(rig.id);
    try {
      await base44.entities.RigLiveFeed.update(rig.id, {
        rig_name: rig.rig_name || '',
        theme: rig.theme || 'none',
        featured: !!rig.featured,
        sort_order: Number(rig.sort_order || 0),
      });
      load();
    } catch (e) {
      alert('Save failed: ' + (e.message || 'error'));
    } finally {
      setSaving(null);
    }
  };

  const addRig = async () => {
    const key = newKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (!key) return;
    setAdding(true);
    try {
      await base44.entities.RigLiveFeed.create({
        rig_key: key,
        rig_name: key.toUpperCase().replace('_', ' '),
        sort_order: rigs.length + 1,
        theme: 'none',
        featured: false,
      });
      setNewKey('');
      load();
    } catch (e) {
      alert('Add failed: ' + (e.message || 'error'));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (rig) => {
    if (!confirm(`Delete rig slot "${rig.rig_key}"? The pusher will recreate it on its next packet.`)) return;
    try {
      await base44.entities.RigLiveFeed.delete(rig.id);
      load();
    } catch (e) {
      alert('Delete failed: ' + (e.message || 'error'));
    }
  };

  const downloadZip = async (rig) => {
    setZipping(rig.id);
    try {
      await downloadRigZip({
        rigKey: rig.rig_key,
        rigName: rig.rig_name || rig.rig_key,
        sim,
        token,
      });
    } catch (e) {
      alert('Could not build zip: ' + (e.message || 'error'));
    } finally {
      setZipping(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-3">
      {/* Token + sim config */}
      <div className="border border-primary/20 bg-primary/[0.03] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-primary" />
          <p className="text-white font-bold text-xs tracking-widest uppercase">Rig PC Setup .zip</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-7">
            <LockedField
              label="Telemetry Token (shared secret)"
              value={token}
              onSave={onTokenChange}
              validate={(v) => (v ? '' : 'Enter the telemetry token before saving.')}
              mask
              emptyHint="Not set — click Edit to enter it"
              helper="Same value as the LIVE_TELEMETRY_TOKEN app secret. Locked against stray typing — click Edit to change it."
            />
          </div>
          <div className="md:col-span-5">
            <label className="block text-white/30 text-[9px] font-bold tracking-widest uppercase mb-1">Sim (for all rigs)</label>
            <select
              value={sim}
              onChange={(e) => saveSim(e.target.value)}
              className="w-full bg-black/40 border border-white/10 focus:border-primary/50 text-white text-xs px-3 py-2.5 outline-none"
            >
              {SIM_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>
        {token ? (
          <div className="flex items-center gap-1.5 text-green-400 text-[10px] mt-2">
            <CheckCircle2 className="w-3 h-3" /> Token saved — launchers below will be pre-filled automatically.
          </div>
        ) : (
          <p className="text-amber-400/80 text-[10px] mt-2">⚠ Token not set — the .bat will include a placeholder for you to fill in on the rig PC.</p>
        )}
      </div>

      {/* Rig rows — table header */}
      <div className="hidden md:grid grid-cols-[110px_1fr_120px_60px_80px_130px] gap-2 px-3 py-1.5 text-white/30 text-[9px] font-bold tracking-widest uppercase">
        <span>Rig Key</span>
        <span>Display Name</span>
        <span>Theme</span>
        <span>Order</span>
        <span className="text-center">Featured</span>
        <span className="text-right">Actions</span>
      </div>

      {/* Rig rows */}
      <div className="space-y-1.5">
        {rigs.map((rig) => (
          <div key={rig.id} className="grid grid-cols-1 md:grid-cols-[110px_1fr_120px_60px_80px_130px] gap-2 items-center border border-white/10 bg-black/30 px-3 py-2">
            <input value={rig.rig_key} disabled className="w-full bg-black/40 border border-white/10 text-white/40 text-xs font-mono px-2.5 py-2" />
            <input value={rig.rig_name || ''} onChange={(e) => updateField(rig.id, 'rig_name', e.target.value)} placeholder="e.g. Sabelt AMG Rig" className="w-full bg-black/40 border border-white/10 focus:border-primary/50 text-white text-xs px-2.5 py-2 outline-none" />
            <select value={rig.theme || 'none'} onChange={(e) => updateField(rig.id, 'theme', e.target.value)} className="w-full bg-black/40 border border-white/10 focus:border-primary/50 text-white text-xs px-2 py-2 outline-none">
              {THEMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input type="number" value={rig.sort_order || 0} onChange={(e) => updateField(rig.id, 'sort_order', e.target.value)} className="w-full bg-black/40 border border-white/10 focus:border-primary/50 text-white text-xs px-2 py-2 outline-none text-center" />
            <div className="flex items-center justify-center">
              <button
                onClick={() => updateField(rig.id, 'featured', !rig.featured)}
                className="p-1.5 transition-colors"
                title={rig.featured ? 'Featured' : 'Not featured'}
              >
                <Star className={`w-4 h-4 ${rig.featured ? 'text-primary fill-primary' : 'text-white/30'}`} />
              </button>
            </div>
            <div className="flex items-center justify-end gap-1">
              <button onClick={() => downloadZip(rig)} disabled={zipping === rig.id} title="Download setup .zip" className="p-2 text-white/50 hover:text-primary transition-colors disabled:opacity-40">
                {zipping === rig.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => save(rig)} disabled={saving === rig.id} title="Save" className="p-2 text-white/50 hover:text-primary transition-colors disabled:opacity-40">
                {saving === rig.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => remove(rig)} title="Delete rig" className="p-2 text-white/50 hover:text-red-400 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add new rig slot */}
      <div className="border border-dashed border-white/10 p-3 flex items-center gap-2">
        <Plus className="w-4 h-4 text-primary shrink-0" />
        <input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="new rig_key e.g. rig_11" className="flex-1 bg-black/40 border border-white/10 focus:border-primary/50 text-white text-xs font-mono px-3 py-2 outline-none" />
        <button onClick={addRig} disabled={adding || !newKey.trim()} className="inline-flex items-center gap-1.5 bg-primary/15 border border-primary/30 hover:bg-primary hover:border-primary text-white text-[10px] font-bold tracking-widest px-3 py-2 transition-colors disabled:opacity-40">
          {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} ADD RIG
        </button>
      </div>
      <p className="text-white/25 text-[11px]">
        Rigs appear automatically when their pusher first reports. Download <Download className="inline w-3 h-3" /> bakes the token + sim into a ready-to-run .zip.
      </p>
    </div>
  );
}